import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';

/**
 * Onglet Securite du dashboard Analytics
 * Affiche les stats de protection brute-force et permet de debloquer des IPs
 */
const SecurityTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unblocking, setUnblocking] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/security-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des stats');
      }

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    // Rafraichir toutes les 30 secondes
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleUnblock = (ip) => {
    setConfirmModal({
      show: true,
      title: 'Débloquer l\'IP',
      message: `Êtes-vous sûr de vouloir débloquer l'IP ${ip} ?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          setUnblocking(ip);
          const response = await fetch(`${API_BASE_URL}/api/admin/blocked-ips/${encodeURIComponent(ip)}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            fetchStats();
          }
        } catch (err) {
          console.error('Erreur deblocage:', err);
        } finally {
          setUnblocking(null);
        }
      }
    });
  };

  const getStatusColor = (current, threshold) => {
    const ratio = current / threshold;
    if (ratio >= 1) return '#dc3545';
    if (ratio >= 0.5) return '#ffc107';
    return '#28a745';
  };

  const getStatusIcon = (current, threshold) => {
    const ratio = current / threshold;
    if (ratio >= 1) return '🔴';
    if (ratio >= 0.5) return '🟡';
    return '🟢';
  };

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        Chargement des statistiques de securite...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#dc3545',
        background: '#fff5f5',
        borderRadius: '8px'
      }}>
        Erreur: {error}
        <br />
        <button
          onClick={fetchStats}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Reessayer
        </button>
      </div>
    );
  }

  const { summary, rateLimiterStats, blockedIPs, recentAttempts } = stats || {};

  return (
    <div>
      {/* Indicateurs temps reel */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Erreurs 500 */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderLeft: `4px solid ${getStatusColor(summary?.lastHour?.errors500 || 0, summary?.thresholds?.errors500 || 10)}`
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Erreurs 500 (1h)
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            {getStatusIcon(summary?.lastHour?.errors500 || 0, summary?.thresholds?.errors500 || 10)}{' '}
            {summary?.lastHour?.errors500 || 0}
            <span style={{ fontSize: '14px', color: '#999', fontWeight: 'normal' }}>
              {' '}/ {summary?.thresholds?.errors500 || 10}
            </span>
          </div>
        </div>

        {/* Echecs login */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderLeft: `4px solid ${getStatusColor(summary?.lastHour?.failedLogins || 0, summary?.thresholds?.failedLogins || 20)}`
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Echecs login (1h)
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            {getStatusIcon(summary?.lastHour?.failedLogins || 0, summary?.thresholds?.failedLogins || 20)}{' '}
            {summary?.lastHour?.failedLogins || 0}
            <span style={{ fontSize: '14px', color: '#999', fontWeight: 'normal' }}>
              {' '}/ {summary?.thresholds?.failedLogins || 20}
            </span>
          </div>
        </div>

        {/* IPs bloquees */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderLeft: `4px solid ${getStatusColor(summary?.lastHour?.blockedIPs || 0, summary?.thresholds?.blockedIPs || 5)}`
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            IPs bloquees (1h)
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            {getStatusIcon(summary?.lastHour?.blockedIPs || 0, summary?.thresholds?.blockedIPs || 5)}{' '}
            {summary?.lastHour?.blockedIPs || 0}
            <span style={{ fontSize: '14px', color: '#999', fontWeight: 'normal' }}>
              {' '}/ {summary?.thresholds?.blockedIPs || 5}
            </span>
          </div>
        </div>

        {/* Stats 24h */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderLeft: '4px solid #667eea'
        }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Connexions (24h)
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            {rateLimiterStats?.last24h?.successful || 0}
            <span style={{ fontSize: '14px', color: '#28a745', fontWeight: 'normal' }}> OK</span>
            {' / '}
            {rateLimiterStats?.last24h?.failed || 0}
            <span style={{ fontSize: '14px', color: '#dc3545', fontWeight: 'normal' }}> KO</span>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div style={{
        background: '#f8f9fa',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '13px',
        color: '#666'
      }}>
        <strong>Configuration :</strong>{' '}
        Blocage apres {rateLimiterStats?.config?.MAX_ATTEMPTS || 5} echecs,{' '}
        duree {rateLimiterStats?.config?.BLOCK_DURATION_MINUTES || 15} min,{' '}
        fenetre {rateLimiterStats?.config?.WINDOW_MINUTES || 60} min
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* IPs bloquees */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #eee',
            fontWeight: '600',
            color: '#333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>IPs actuellement bloquees</span>
            <span style={{
              background: blockedIPs?.length > 0 ? '#dc3545' : '#28a745',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '12px'
            }}>
              {blockedIPs?.length || 0}
            </span>
          </div>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {blockedIPs?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>IP</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Expire</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedIPs.map((ip, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px', fontFamily: 'monospace' }}>{ip.ip_address}</td>
                      <td style={{ padding: '10px', color: '#666' }}>
                        {new Date(ip.blocked_until).toLocaleString('fr-FR')}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleUnblock(ip.ip_address)}
                          disabled={unblocking === ip.ip_address}
                          style={{
                            padding: '4px 12px',
                            background: unblocking === ip.ip_address ? '#ccc' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: unblocking === ip.ip_address ? 'wait' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {unblocking === ip.ip_address ? '...' : 'Debloquer'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                Aucune IP bloquee
              </div>
            )}
          </div>
        </div>

        {/* Dernieres tentatives */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #eee',
            fontWeight: '600',
            color: '#333'
          }}>
            Dernieres tentatives de connexion
          </div>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            {recentAttempts?.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Heure</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Email</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Status</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee' }}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAttempts.slice(0, 20).map((attempt, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px', color: '#666' }}>
                        {new Date(attempt.created_at).toLocaleTimeString('fr-FR')}
                      </td>
                      <td style={{ padding: '10px' }}>{attempt.email || '-'}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {attempt.success ? (
                          <span style={{ color: '#28a745' }}>OK</span>
                        ) : (
                          <span style={{ color: '#dc3545' }}>Echec</span>
                        )}
                      </td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '11px', color: '#666' }}>
                        {attempt.ip_address}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                Aucune tentative recente
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Derniere mise a jour */}
      <div style={{
        marginTop: '16px',
        textAlign: 'right',
        fontSize: '12px',
        color: '#999'
      }}>
        Derniere mise a jour : {stats?.timestamp ? new Date(stats.timestamp).toLocaleString('fr-FR') : '-'}
        {' '}
        <button
          onClick={fetchStats}
          style={{
            padding: '4px 8px',
            background: 'transparent',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Actualiser
        </button>
      </div>

      {/* Modal de confirmation */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                style={{ padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmModal.onConfirm}
                style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                Débloquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;
